import { PharmaStpFileModel } from "../../../../data-model/nycbsPharmaOrder";
import { formatDob } from "../../../../pages/callingWorklist/table/utils/formatDob";
import { formatPatientName } from "../../../../pages/callingWorklist/table/utils/formatPatientName";
import DetailField from "../components/detailField";

interface CaseDetailsContentProps {
  data: PharmaStpFileModel;
  /** Dashed rule between the patient and drug groups. Off in the side panel. */
  showGroupDivider?: boolean;
}

/** Placeholder shown when a case detail is missing on the order row. */
const EMPTY_VALUE = "--";

/**
 * Returns a trimmed field value, falling back to a placeholder when empty.
 *
 * @input value - Raw value from the order row
 * @return Display-ready value
 */
const withFallback = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  return trimmed && trimmed !== EMPTY_VALUE ? trimmed : EMPTY_VALUE;
};

/**
 * Case Details tab of the non-EPA modal: patient identity, drug,
 * CMM ID and the call reason that triggered the call.
 *
 * @param props.data - Order row the modal was opened for
 */
const CaseDetailsContent = (props: CaseDetailsContentProps) => {
  const { data } = props;
  const groupDivider =
    props.showGroupDivider === false
      ? ""
      : "border-b border-dashed border-primaryGray-14 pb-6";
  const patientName = withFallback(formatPatientName(data?.patient_name ?? ""));
  const mrn = withFallback(data?.patient_mrn);
  const callReason = withFallback(data?.non_epa_reason);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className={`grid grid-cols-2 gap-4 ${groupDivider}`}>
        <DetailField
          label="Patient Name"
          value={
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate">{patientName}</span>
              {mrn !== EMPTY_VALUE && (
                <span className="rounded bg-primaryGray-16 px-2 py-0.5 text-tiny text-primaryGray-3">
                  {mrn}
                </span>
              )}
            </div>
          }
        />
        <DetailField label="Date of Birth" value={formatDob(data?.dob)} />
      </div>

      <div className="grid grid-cols-2 gap-4 border-b border-dashed border-primaryGray-14 pb-6">
        <DetailField label="Drug" value={withFallback(data?.drug)} />
        <DetailField
          label="CMM ID"
          value={
            <span className="font-bold">
              {withFallback(data?.covermymed_id)}
            </span>
          }
        />
      </div>

      <div className="flex flex-col gap-2 rounded-md bg-primaryGray-16 p-4">
        <p className="m-0 text-tiny font-semibold text-primaryGray-9">
          Call Reason
        </p>
        <p className="m-0 whitespace-pre-wrap text-small leading-small text-primaryGray-1">
          {callReason}
        </p>
      </div>
    </div>
  );
};

export default CaseDetailsContent;
